import React from 'react';
import { authConfigured, currentAccount, signIn } from '../lib/auth';
import AccountMenu from './AccountMenu';
import { contractorIdentity, contractorSignOut } from '../lib/contractorAuth';
import { forgetSignIn, rememberedSignIn } from '../lib/signInMemory';
import { useMe, useMeStatus } from '../lib/me';
import './SignIn.css';

/**
 * Sign in, and show who the *server* thinks you are.
 *
 * Deliberately not the browser's opinion: the role shown here is whatever
 * /auth/me resolved from the token, which is the only one that decides
 * anything. Until now the app read its own role out of localStorage, where it
 * defaulted to 'admin'.
 *
 * ONE ANSWER, SHARED. This used to make its own second call to /auth/me with
 * its own "Checking…" and error state, so the header and the router could
 * disagree about who you are - and did, whenever one call failed and the
 * other did not. It reads lib/me now, like everything else.
 */

/**
 * "Could not reach the server, retrying" - the small line for the moment
 * /auth/me cannot be reached (lib/me keeps the last answer and tries again
 * with a growing wait). Exported so the chromeless pages, which have no
 * header for SignIn, can show it in their corner too.
 */
export function ServerReachNote() {
  const status = useMeStatus();
  if (!status.failed) return null;
  return (
    <span className="signin-note signin-reach" role="status" aria-live="polite" data-attempt={status.attempt}>
      Could not reach the server — retrying…
    </span>
  );
}

export default function SignIn() {
  const me = useMe();
  const loading = me === null || me === undefined;

  /*
   * A CONTRACTOR IS NOT SIGNED OUT. THEY ARE SIGNED IN AS SOMEBODY ELSE.
   *
   * Gordon: "(and if a contractor is not on O365 why does he have a button
   * sign in with microsoft?)"
   *
   * Because this only ever asked /auth/me, which answers about Entra and
   * nothing else. A contractor has no Entra account - they sign in with a
   * code sent to their own address - so /auth/me says "not signed in" and
   * this corner offered them the one button that could never work for them.
   * It was invisible while the portal hid the navbar. The moment a contractor
   * could reach /chat, which was the whole point of the chat work, it was
   * sitting in the header of every page they could see.
   *
   * So the contractor's own session is checked before concluding nobody is
   * here. They get the same account menu as everybody else - their name, the
   * theme, Sign out - which is the other half of standardising the header
   * rather than giving them a third variant of it.
   *
   * Checked before authConfigured, too: whether the firm has Entra set up has
   * no bearing on whether this contractor is signed in.
   *
   * `isContractor: true`, so the menu knows who it is drawing for: it hides
   * Meetings from a contractor on purpose (a contractor is invited to a
   * meeting from the bell or the invitation, and the bar does not offer the
   * page either - App.js says so), and without the flag it could not tell.
   */
  const contractor = contractorIdentity();
  if (!loading && !me?.signedIn && contractor) {
    return (
      <div className="signin-box">
        <ServerReachNote />
        <AccountMenu
          me={{ name: contractor.name, isAdmin: false, isContractor: true, signedIn: true }}
          onSignOut={() => {
            contractorSignOut();
            // Signed out on purpose: the front door no longer opens on their
            // address (lib/signInMemory). Only a code sign-in is forgotten -
            // not a member of staff who uses the same machine.
            forgetSignIn('code');
            // A full load rather than a route change: the shell decides which
            // chrome to draw from that token, and this is the moment it stops
            // existing.
            window.location.assign('/contractor-login');
          }}
        />
      </div>
    );
  }

  if (!authConfigured) {
    return <span className="signin-note">Sign-in is not configured</span>;
  }
  if (loading) return <span className="signin-note">Checking…</span>;

  if (!me?.signedIn) {
    // An account present on the browser but rejected by the server is a
    // different problem from not being signed in, and says so. A server that
    // could not be reached is a third, and ServerReachNote says that one.
    const error = !me.failed && currentAccount()
      ? (me.reason || 'Microsoft signed you in, but this app would not accept it')
      : null;
    return (
      <div className="signin-box">
        <ServerReachNote />
        {error && <span className="signin-error" role="alert">{error}</span>}
        <button type="button" className="btn btn-primary btn-small"
                onClick={() => {
                  const memo = rememberedSignIn();
                  signIn(memo?.method === 'microsoft' ? { hint: memo.hint } : {});
                }}>
          Sign in with Microsoft
        </button>
      </div>
    );
  }

  // Signed in, the header shows one thing: who you are. Sign out, the theme
  // and "View as…" all sit under it - they are settings for this account, and
  // three loose controls in the corner was three things to explain.
  //
  // What the menu does NOT hide is the state: the name, the Admin badge and
  // the "Viewing as" badge stay on the trigger, because the server's answer
  // about who it is answering as has to be readable without opening anything.
  return (
    <div className="signin-box">
      <ServerReachNote />
      <AccountMenu me={me} />
    </div>
  );
}
